/**
 * ObjectInfoPanel React component
 * 
 * @author    Jens Gruschel
 * @copyright © 2020 contexagon GmbH
 */


// React
import React from 'react';
import {connect} from 'react-redux';

// 3rd party
import { useDrop } from 'react-dnd'

// application
import {DRAGGABLE_MENU_OBJECT_TYPE} from "./DraggableMenuObject";
import {patchServerUrl} from "../../config";
import {selectFieldValue, selectFieldName} from "../../redux/types/selectors";
import {selectTranslation} from "../../redux/configuration/selectors";
import {findTaggedImage} from "../../utils";
import MediaPanel from "../base/MediaPanel";
import {openMediaOverlayForUrl} from "../../operations/view";
import MediaViewerOverlay from "../base/MediaViewerOverlay";


class ObjectInfoPanel extends React.Component {

    constructor(props) {
        super(props);
        this.scrollRef = React.createRef();
    }

    scrollBy(amount) {
        if (this.scrollRef.current) {
            this.scrollRef.current.scrollBy({ top: amount, behavior: 'smooth' });
        }
    }

    render() {

        console.log("ObjectInfoPanel.render()", this.props);

        // the outermost div takes care of the layout,
        // the next div does nothing, but can be referenced by customized CSS,
        // the inner div controls the background (as defined for the specific slide),
        // the title and markdown widget within both have another class name for customized CSS

        return <div style={{position: "absolute", top: 0, bottom: 0, left: 0, right: 0}}>
            <style>
                {this.props.stylesheet}
            </style>

            <div className="objectinfopanel" ref={this.scrollRef} style={{position: "absolute", top: 0, bottom: 0, left: 0, right: 0}}>
                <div className="objectinfolayout">
                    <h1 className="objectinfotitle">{this.props.title}</h1>
                    <div className="objectinfocontainer">
                    {
                        this.props.subjects.map((subject, index) => <ObjectInfo
                            key={(subject && (subject.id + "/" + index)) || index}
                            subject={subject}
                            index={index}
                            fields={this.props.fields[index]}
                            referencedObjects={this.props.referencedObjects[index]}
                            referencedMediaObjects={this.props.referencedMediaObjects}
                            imagetags={this.props.imagetags}
                            openMediaOverlayForUrl={this.props.openMediaOverlayForUrl}
                        />)
                    }
                    </div>
                </div>
            </div>
            <MediaViewerOverlay/>
        </div>;
    }

}




function ObjectInfo(props) {

    const [expandedField, setExpandedField] = React.useState(null);
    const expandedRef = React.useRef(null);

    // Auto-scroll to the expanded accordion section after it renders
    React.useEffect(() => {
        if (expandedField && expandedRef.current) {
            // Small delay to let the content render before scrolling
            setTimeout(() => {
                if (expandedRef.current) {
                    expandedRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
            }, 50);
        }
    }, [expandedField]);

    const { subject, index, fields, referencedObjects, referencedMediaObjects, imagetags } = props;

    const taggedImage = subject && findTaggedImage(subject, referencedObjects, imagetags);
    const taggedImageUrl = patchServerUrl((taggedImage && taggedImage.fields && taggedImage.fields.url) || (subject && subject.imageurl));

    const [{ isOver, canDrop }, drop] = useDrop({
        accept: DRAGGABLE_MENU_OBJECT_TYPE,
        canDrop: () => true,
		drop: (item, monitor) => ({
            item: item,
            index: index
        }),
		collect: monitor => ({
            isOver: !!monitor.isOver(),
            canDrop: !!monitor.canDrop()
		}),
	});

    const accordionStart = fields.findIndex(f => f.id === '#accordion#');
    const preFields = accordionStart >= 0 ? fields.slice(0, accordionStart) : fields;
    const accordionFields = accordionStart >= 0 ? fields.slice(accordionStart + 1) : [];

    function renderFieldContent(field) {
        if (field.id === "medialist" && field.value) {
            return field.value.split(",").map(mediaId => {
                const id = mediaId.trim();
                const mediaItem = findMediaItem(id, referencedMediaObjects);
                if (mediaItem) {
                    const mediaUrl = patchServerUrl(mediaItem.fields.url);
                    return <MediaPanel key={id} url={mediaUrl} showControls={true} onClick={() => props.openMediaOverlayForUrl(mediaUrl, mediaItem.fields.title)}/>;
                }
                return null;
            });
        }
        return field.value;
    }

    return <div
        ref={drop}
        className={"objectinfo" + (!subject ? " missing" : "") + (canDrop ? " dragging" : "") + (isOver ? " dropping" : "")}
    >
        <div className="objectinfoimage">
            {subject && subject.imageurl && <img src={taggedImageUrl}/>}
        </div>
        <ul>
            {preFields.map(field => (
                <li key={field.id} className={"field-" + field.id}>
                    <label>{field.name}</label>
                    <p>{renderFieldContent(field)}</p>
                </li>
            ))}
        </ul>
        {accordionFields.map(field => (
            <div key={field.id} className="accordion"
                ref={expandedField === field.id ? expandedRef : null}
                onClick={() => setExpandedField(f => f === field.id ? null : field.id)}>
                <div className="accordionSummary">{field.name}</div>
                {expandedField === field.id && <div className="accordionDetails">{renderFieldContent(field)}</div>}
            </div>
        ))}
    </div>;
}


const findMediaItem = (id, referencedMediaObjects) => {

    for(var key of Object.keys(referencedMediaObjects)) {

        let mediaObject = referencedMediaObjects[key];
        if(id === mediaObject.id) {
            return mediaObject;
        }
    }
};


const mapStateToProps = (state, props) => {

    const quantity = props.quantity || 1;
    const fieldIds = props.fieldIds || [];

    const missing = Math.max(quantity - state.cbox.context.subjects.length, 0);
    const subjects = [...state.cbox.context.subjects.slice(0, quantity), ...Array(missing).fill(null)];
    const referencedObjects = [...state.cbox.context.referencedObjects.slice(0, quantity), ...Array(missing).fill(null)];
    const fields = subjects.map(
        subject => fieldIds.map(
            fieldId => ({
                id: fieldId,
                name: (subject && selectFieldName(state.types, subject.type, fieldId)) || fieldId,
                value: (subject && selectTranslation(state.configuration, selectFieldValue(state.types, {}, subject, fieldId, true))) || ""
            })
        )
    );

    return {
        subjects: subjects,
        referencedObjects: referencedObjects,
        referencedMediaObjects: props.referencedObjects,
        fields: fields,
    };
};

export default connect(mapStateToProps, {openMediaOverlayForUrl})(ObjectInfoPanel);